import React from 'react';
import { track } from '@amplitude/analytics-browser';
import {
  Box,
  FileUploader,
  Icon,
  IconButton,
  Link,
  Text,
} from '@nimbus-ds/components';
import { FileIcon, TrashIcon } from '@nimbus-ds/icons';
import { ThumbnailWithAction } from '@nimbus-ds/patterns';
import { useTranslationWithPrefix } from 'commons/utils';
import ChargebackDocumentUploaderSkeleton from './ChargebackDocumentUploaderSkeleton';
import { useChargebackDocumentUploader } from './useChargebackDocumentUploader';

export interface ChargebackDocumentUploaderProps {
  documentName: string;
  onSuccess: (key: string, duration: number) => void;
  onFileError: (text: string) => void;
  onUploadError: () => void;
  onDelete: () => void;
  helperText: string;
  accept: string;
  disabled?: boolean;
  ariaLabel: string;
  fullWidth?: boolean;
  isMobile?: boolean;
  maxDocumentSize: number;
  orderId: string;
  viewDocumentTrackingTag?: string;
}

function ChargebackDocumentUploader({
  ariaLabel,
  documentName,
  helperText,
  onSuccess,
  onFileError,
  onUploadError,
  onDelete,
  fullWidth,
  isMobile,
  disabled,
  accept,
  maxDocumentSize,
  orderId,
  viewDocumentTrackingTag = 'pagonube-cbks-view-document-click',
}: ChargebackDocumentUploaderProps): JSX.Element {
  const { t } = useTranslationWithPrefix('chargebackUpload');
  const { isLoading, fileName, blobUrl, handleFileChange, handleFileDelete } =
    useChargebackDocumentUploader({
      documentName,
      onSuccess,
      onFileError,
      onUploadError,
      onDelete,
      maxDocumentSize,
      accept,
      orderId,
    });

  if (isLoading) {
    return <ChargebackDocumentUploaderSkeleton />;
  }

  if (fileName?.toLowerCase().endsWith('.pdf')) {
    return (
      <Box display="flex" flexDirection="column" gap="2">
        <Box
          borderStyle="dashed"
          borderColor="primary-interactive"
          borderWidth="1"
          borderRadius="2"
          backgroundColor="neutral-surface"
          width={fullWidth && !isMobile ? '350px' : '100%'}
          height="180px"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <Text color="primary-interactive" fontWeight="bold">
            {fileName}
          </Text>
          <IconButton
            source={<TrashIcon color="black" />}
            size="2rem"
            onClick={handleFileDelete}
            aria-label={t('delete')}
          />
        </Box>
        {blobUrl ? (
          <Link
            as="a"
            appearance="primary"
            href={blobUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track(viewDocumentTrackingTag)}
          >
            <Icon source={<FileIcon size="small" />} color="currentColor" />
            {t('seeDocument')}
          </Link>
        ) : null}
      </Box>
    );
  }

  if (blobUrl) {
    return (
      <Box display="flex" flexDirection="column" gap="2">
        <Box width={fullWidth && !isMobile ? '350px' : '100%'}>
          <ThumbnailWithAction
            thumbnail={{
              src: blobUrl,
              alt: ariaLabel,
              aspectRatio: '2/1',
              width: '100%',
              height: '180px',
              className: 'document-uploader-thumbnail-img',
            }}
          >
            <IconButton
              source={<TrashIcon color="black" />}
              size="2rem"
              onClick={handleFileDelete}
              aria-label={t('delete')}
            />
          </ThumbnailWithAction>
        </Box>
      </Box>
    );
  }

  return (
    <Box width="100%" display="flex" flexDirection="column" gap="2">
      <FileUploader
        aspectRatio="none"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) handleFileChange(file);
        }}
        placeholder={helperText}
        disabled={disabled}
        accept={accept}
        id={documentName}
        data-testid={`file-input-${documentName}`}
        height="180px"
      />
    </Box>
  );
}

ChargebackDocumentUploader.Skeleton = ChargebackDocumentUploaderSkeleton;
export default ChargebackDocumentUploader;
